import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI, constantsAPI, preferencesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Heart, ArrowLeft, ArrowRight, Camera, Plus, X, Check, Flame } from 'lucide-react';
import { ScoreLogo } from '../components/Logo';
import { toast } from 'sonner';

export const ProfileSetup = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [prompts, setPrompts] = useState({});

  const [profile, setProfile] = useState({
    gender: '',
    age: '',
    sports: [],
    height: '',
    location: '',
    dating_intentions: '',
    ethnicity: '',
    smoking: '',
    drinking: '',
    children: '',
    family_plans: '',
    pets: '',
    zodiac: '',
    work: '',
    job_title: '',
    school: '',
    education_level: '',
    religious_beliefs: '',
    hometown: '',
    political_views: '',
    drugs: '',
    bio: '',
    photos: [],
    prompts: []
  });

  const [preferences, setPreferences] = useState({
    interested_in: 'Everyone',
    max_distance: 50,
    age_range_min: 18,
    age_range_max: 50
  });

  const [selectedSport, setSelectedSport] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');

  useEffect(() => {
    loadConstants();
  }, []);

  const loadConstants = async () => {
    try {
      const [sportsRes, promptsRes] = await Promise.all([
        constantsAPI.getSports(),
        constantsAPI.getPrompts()
      ]);
      setSports(sportsRes.data.sports);
      setPrompts(promptsRes.data.prompts);
    } catch (error) {
      console.error('Failed to load constants:', error);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (profile.photos.length < 6) {
        setProfile({ ...profile, photos: [...profile.photos, reader.result] });
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (index) => {
    const newPhotos = [...profile.photos];
    newPhotos.splice(index, 1);
    setProfile({ ...profile, photos: newPhotos });
  };

  const addSport = () => {
    if (selectedSport && selectedLevel && profile.sports.length < 3) {
      const exists = profile.sports.some(s => s.sport === selectedSport);
      if (!exists) {
        setProfile({
          ...profile,
          sports: [...profile.sports, { sport: selectedSport, level: selectedLevel }]
        });
        setSelectedSport('');
        setSelectedLevel('');
      }
    }
  };

  const removeSport = (index) => {
    const newSports = [...profile.sports];
    newSports.splice(index, 1);
    setProfile({ ...profile, sports: newSports });
  };

  const addPrompt = (category, promptText, answer) => {
    if (profile.prompts.length >= 3) {
      toast.error('Maximum 3 prompts allowed');
      return;
    }
    const exists = profile.prompts.some(p => p.prompt === promptText);
    if (!exists && answer.trim()) {
      setProfile({
        ...profile,
        prompts: [...profile.prompts, { category, prompt: promptText, answer: answer.trim() }]
      });
    }
  };

  const removePrompt = (index) => {
    const newPrompts = [...profile.prompts];
    newPrompts.splice(index, 1);
    setProfile({ ...profile, prompts: newPrompts });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const profileData = {
        ...profile,
        age: parseInt(profile.age)
      };
      await profileAPI.create(profileData);
      await preferencesAPI.save(preferences);
      
      updateUser({ ...user, profile_complete: true });
      toast.success('Profile created!');
      navigate('/discover');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return profile.gender && profile.age && profile.sports.length > 0;
      case 2: return profile.photos.length >= 1;
      case 3: return true;
      case 4: return preferences.interested_in;
      default: return true;
    }
  };

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const levelColors = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-yellow-500',
    elite: 'bg-orange-500',
    college_pro: 'bg-red-500'
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ScoreLogo size={44} />
            <span className="font-display font-black text-xl text-gray-900 tracking-tighter">SCORE</span>
          </div>
          <span className="font-body text-gray-500">Step {step} of {totalSteps}</span>
        </div>

        {/* Progress */}
        <Progress value={progress} className="mb-8 h-2" />

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-2xl uppercase text-gray-900">Basic Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="font-body text-gray-700">Gender</Label>
                <Select value={profile.gender} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
                  <SelectTrigger data-testid="gender-select" className="bg-white border-gray-200">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-gray-700">Age</Label>
                <Input
                  data-testid="age-input"
                  type="number"
                  min={18}
                  max={100}
                  placeholder="Your age"
                  className="bg-white border-gray-200"
                  value={profile.age}
                  onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                />
              </div>

              <div className="space-y-4">
                <Label className="font-body text-gray-700">Sports (up to 3)</Label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {profile.sports.map((s, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100"
                    >
                      <span className={`w-2 h-2 rounded-full ${levelColors[s.level]}`} />
                      <span className="font-body text-sm text-gray-700">{s.sport}</span>
                      <button onClick={() => removeSport(i)} className="text-gray-400 hover:text-primary">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {profile.sports.length < 3 && (
                  <div className="flex gap-2">
                    <Select value={selectedSport} onValueChange={setSelectedSport}>
                      <SelectTrigger data-testid="sport-select" className="flex-1 bg-white border-gray-200">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {sports.map((sport) => (
                          <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                      <SelectTrigger data-testid="level-select" className="w-40 bg-white border-gray-200">
                        <SelectValue placeholder="Level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="elite">Elite</SelectItem>
                        <SelectItem value="college_pro">College/Pro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      data-testid="add-sport-btn"
                      size="icon" 
                      onClick={addSport}
                      disabled={!selectedSport || !selectedLevel}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-gray-700">Height</Label>
                  <Input
                    data-testid="height-input"
                    placeholder="e.g., 5'10&quot;"
                    className="bg-white border-gray-200"
                    value={profile.height}
                    onChange={(e) => setProfile({ ...profile, height: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-gray-700">Location</Label>
                  <Input
                    data-testid="location-input"
                    placeholder="City, State"
                    className="bg-white border-gray-200"
                    value={profile.location}
                    onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Photos */}
        {step === 2 && (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-2xl uppercase text-gray-900">Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-body text-gray-500 mb-6">Add up to 6 photos. First photo will be your main profile picture.</p>
              <div className="grid grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div 
                    key={i}
                    className="aspect-[3/4] rounded-xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center relative overflow-hidden"
                  >
                    {profile.photos[i] ? (
                      <>
                        <img 
                          src={profile.photos[i]} 
                          alt={`Photo ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button 
                          onClick={() => removePhoto(i)}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-primary transition-colors text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {i === 0 && (
                          <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-primary text-white text-xs font-body">
                            Main
                          </div>
                        )}
                      </>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center gap-2 text-gray-400 hover:text-primary transition-colors">
                        <Camera className="w-8 h-8" />
                        <span className="text-xs font-body">Add Photo</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handlePhotoUpload}
                          data-testid={`photo-upload-${i}`}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Prompts & Bio */}
        {step === 3 && (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-2xl uppercase text-gray-900">About You</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="font-body text-gray-700">Bio</Label>
                <Textarea
                  data-testid="bio-input"
                  placeholder="Tell others about yourself..."
                  className="bg-white border-gray-200 min-h-24"
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                />
              </div>

              <div className="space-y-4">
                <Label className="font-body text-gray-700">Prompts ({profile.prompts.length}/3)</Label>
                
                {/* Selected Prompts */}
                {profile.prompts.map((p, i) => (
                  <div key={i} className="p-4 rounded-xl bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-body text-sm text-gray-500">{p.prompt}</span>
                      <button onClick={() => removePrompt(i)} className="text-gray-400 hover:text-primary">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="font-body text-gray-700">{p.answer}</p>
                  </div>
                ))}

                {/* Add Prompt */}
                {profile.prompts.length < 3 && (
                  <PromptSelector 
                    prompts={prompts} 
                    onSelect={addPrompt}
                  />
                )}
              </div>

              {/* Optional Fields */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="font-display text-lg uppercase mb-4 text-gray-900">Optional Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-body text-sm text-gray-600">Job Title</Label>
                    <Input
                      data-testid="job-title-input"
                      placeholder="Your job"
                      className="bg-white border-gray-200"
                      value={profile.job_title}
                      onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-sm text-gray-600">Company</Label>
                    <Input
                      placeholder="Where you work"
                      className="bg-white border-gray-200"
                      value={profile.work}
                      onChange={(e) => setProfile({ ...profile, work: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-sm text-gray-600">School</Label>
                    <Input
                      placeholder="Your school"
                      className="bg-white border-gray-200"
                      value={profile.school}
                      onChange={(e) => setProfile({ ...profile, school: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-sm text-gray-600">Hometown</Label>
                    <Input
                      placeholder="Where you're from"
                      className="bg-white border-gray-200"
                      value={profile.hometown}
                      onChange={(e) => setProfile({ ...profile, hometown: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Preferences */}
        {step === 4 && (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-2xl uppercase text-gray-900">Dating Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="font-body text-gray-700">I'm interested in</Label>
                <Select 
                  value={preferences.interested_in} 
                  onValueChange={(v) => setPreferences({ ...preferences, interested_in: v })}
                >
                  <SelectTrigger data-testid="interested-in-select" className="bg-white border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Men">Men</SelectItem>
                    <SelectItem value="Women">Women</SelectItem>
                    <SelectItem value="Everyone">Everyone</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-gray-700">Age Range: {preferences.age_range_min} - {preferences.age_range_max}</Label>
                <div className="flex gap-4">
                  <Input
                    data-testid="age-min-input"
                    type="number"
                    min={18}
                    max={100}
                    className="bg-white border-gray-200"
                    value={preferences.age_range_min}
                    onChange={(e) => setPreferences({ ...preferences, age_range_min: parseInt(e.target.value) })}
                  />
                  <Input
                    data-testid="age-max-input"
                    type="number"
                    min={18}
                    max={100}
                    className="bg-white border-gray-200"
                    value={preferences.age_range_max}
                    onChange={(e) => setPreferences({ ...preferences, age_range_max: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-gray-700">Maximum Distance: {preferences.max_distance} miles</Label>
                <Input
                  data-testid="max-distance-input"
                  type="range"
                  min={1}
                  max={100}
                  className="bg-white"
                  value={preferences.max_distance}
                  onChange={(e) => setPreferences({ ...preferences, max_distance: parseInt(e.target.value) })}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            data-testid="back-btn"
            variant="outline"
            className="border-gray-200"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {step < totalSteps ? (
            <Button
              data-testid="next-btn"
              className="bg-primary hover:bg-primary/90"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="complete-btn"
              className="bg-primary hover:bg-primary/90"
              onClick={handleSubmit}
              disabled={isLoading || !canProceed()}
            >
              {isLoading ? 'Saving...' : 'Complete Profile'}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Prompt Selector Component
const PromptSelector = ({ prompts, onSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [answer, setAnswer] = useState('');

  const categories = Object.keys(prompts);
  const categoryLabels = {
    personality: 'Personality & Humor',
    conversation: 'Conversation Starters',
    values: 'Values & Compatibility',
    lifestyle: 'Ambition & Lifestyle'
  };

  const handleAdd = () => {
    if (selectedPrompt && answer.trim()) {
      onSelect(selectedCategory, selectedPrompt, answer);
      setSelectedCategory('');
      setSelectedPrompt('');
      setAnswer('');
    }
  };

  return (
    <div className="space-y-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
      <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setSelectedPrompt(''); }}>
        <SelectTrigger data-testid="prompt-category-select" className="bg-white border-gray-200">
          <SelectValue placeholder="Select a category" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((cat) => (
            <SelectItem key={cat} value={cat}>{categoryLabels[cat]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedCategory && (
        <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
          <SelectTrigger data-testid="prompt-select" className="bg-white border-gray-200">
            <SelectValue placeholder="Select a prompt" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {prompts[selectedCategory]?.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedPrompt && (
        <>
          <Textarea
            data-testid="prompt-answer-input"
            placeholder="Your answer..."
            className="bg-white border-gray-200"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
          <Button 
            data-testid="add-prompt-btn"
            size="sm" 
            onClick={handleAdd}
            disabled={!answer.trim()}
          >
            Add Prompt
          </Button>
        </>
      )}
    </div>
  );
};
